import React, { memo, useRef } from "react";

// internal-deps
import { isEmpty } from "@swiggy-private/js-utils";
import { IColors, ISpacing, ITypography } from "@swiggy-private/dls-web-common/dist/components/types.internal";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { EditableRow } from "@swiggy-private/dls-web-recipe-common-web/dist/components/EditableRow";
import { BottomInputHalfCardContent } from "@swiggy-private/dls-web-recipe-common-web/dist/components/BottomInputHalfCardContent";
import { IWidget, useKeyboardState } from "@swiggy-private/checkout-core";

// hooks
import useSpecialRequest from "@dineout-cart-client/hooks/useSpecialRequest";

// constants
import {
    ADD_SPECIAL_REQUEST_ARIA_LABEL,
    CLEAR_SPECIAL_REQUEST_ARIA_LABEL,
    CLEAR_SPECIAL_REQUEST_TEXT,
    DEFAULT_SPECIAL_REQUEST_ANIMATION_TIME_SECS,
    DEFAULT_SPECIAL_REQUEST_CTA_TEXT,
    DEFAULT_SPECIAL_REQUEST_MAX_LENGTH,
    DEFAULT_SPECIAL_REQUEST_TITLE,
} from "@dineout-cart-client/constants";

interface ISpecialRequestProps {
    specialRequestWidgetData?: IWidget;
    onClickSpecialRequest: () => void;
    onImpressionSpecialRequestHalfcard: () => void;
    onClickSubmitSpecialRequest: (instruction: string) => void;
    onClickDeleteSpecialRequest: () => void;
    onSubmitSpecialRequest: (instruction: string) => void;
    onClearSpecialRequestCart: () => void;
}

const SpecialRequest = (props: ISpecialRequestProps) => {
    const {
        specialRequestWidgetData,
        onClickSpecialRequest,
        onImpressionSpecialRequestHalfcard,
        onClickSubmitSpecialRequest,
        onClickDeleteSpecialRequest,
        onSubmitSpecialRequest,
        onClearSpecialRequestCart,
    } = props;

    const {
        specialRequestInfo,
        instruction,
        draft,
        setDraft,
        showSheet,
        handleOpenSheet,
        handleCloseSheet,
        handleSubmit,
        handleRightElementClick,
    } = useSpecialRequest({
        widgetData: specialRequestWidgetData,
        onClick: onClickSpecialRequest,
        onImpression: onImpressionSpecialRequestHalfcard,
        onClickSubmit: onClickSubmitSpecialRequest,
        onClickDelete: onClickDeleteSpecialRequest,
        onSubmit: onSubmitSpecialRequest,
        onClearCart: onClearSpecialRequestCart,
    });
    const containerRef = useRef<HTMLDivElement>(null);

    useKeyboardState(containerRef);

    if (isEmpty(specialRequestInfo)) return null;

    return (
        <>
            <EditableRow
                title={{
                    text: specialRequestInfo?.title || DEFAULT_SPECIAL_REQUEST_TITLE,
                    color: "text_Highest_Emphasis" as keyof IColors,
                    variant: "V2.body2.neutral.medium" as keyof ITypography,
                }}
                description={instruction ? { text: instruction, maxLines: 2 } : undefined}
                left={{ iconName: "edit16", textColor: "text_High_Emphasis" as keyof IColors }}
                right={{
                    iconName: instruction ? undefined : "chevronRight12",
                    textColor: instruction ? undefined : ("text_High_Emphasis" as keyof IColors),
                    text: instruction ? CLEAR_SPECIAL_REQUEST_TEXT : undefined,
                }}
                leftGap={"x3" as keyof ISpacing}
                rightActionAriaLabel={
                    instruction ? CLEAR_SPECIAL_REQUEST_ARIA_LABEL : ADD_SPECIAL_REQUEST_ARIA_LABEL
                }
                onClick={(source) => (source === "rightAction" ? handleRightElementClick() : handleOpenSheet())}
                backgroundColor={"background_Primary" as keyof IColors}
            />

            <HalfCard isOpen={showSheet} onClose={handleCloseSheet} variant="v3">
                <div ref={containerRef}>
                    <BottomInputHalfCardContent
                        value={draft}
                        onChange={setDraft}
                        placeholder={specialRequestInfo?.placeholders}
                        subtext={specialRequestInfo?.subtitle}
                        placeholderAnimationConfig={{
                            interval:
                                (specialRequestInfo?.animationTime || DEFAULT_SPECIAL_REQUEST_ANIMATION_TIME_SECS) *
                                1000,
                        }}
                        maxLength={specialRequestInfo?.maxLength || DEFAULT_SPECIAL_REQUEST_MAX_LENGTH}
                        autoFocus
                        onPrimaryCtaClick={handleSubmit}
                        primaryCtaText={specialRequestInfo?.primaryCtaText || DEFAULT_SPECIAL_REQUEST_CTA_TEXT}
                        isPrimaryCtaDisabled={!draft.trim()}
                    />
                </div>
            </HalfCard>
        </>
    );
};

export default memo(SpecialRequest);
